import { FC } from "react"
import { useTranslation } from "react-i18next"
import { CurrencyIcon } from "@/helpers/currencyIcon"
import { numberSpaceFixer } from "@/helpers/numberSpaceFixer"
import styles from "./WithdrawAmountInput.module.scss"
import type { IWithdrawAmountInputProps } from "./types"

const PERCENT_SHORTCUTS = [10, 25, 50, 75, 100] as const

export const WithdrawAmountInput: FC<IWithdrawAmountInputProps> = ({
  currency,
  balance,
  amount,
  onAmountChange,
  limitFrom,
  limitTo,
  isConfigLoading
}) => {
  const { t } = useTranslation()
  const Icon = CurrencyIcon[currency]

  const handlePercentClick = (percent: number): void => {
    const value = Math.floor(balance * (percent / 100))
    onAmountChange(String(value))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value.replace(/\s/g, "").replace(",", ".")
    if (value === "") {
      onAmountChange("")
      return
    }
    if (!/^\d*\.?\d{0,2}$/.test(value)) return
    const numeric = Number(value)
    if (Number.isFinite(numeric) && numeric > balance) {
      onAmountChange(String(balance))
      return
    }
    onAmountChange(value)
  }

  return (
    <div className={styles.WithdrawAmountInput}>
      <div className={styles.WithdrawAmountInput_header}>
        <div className={styles.WithdrawAmountInput_icon}>
          {Icon && <Icon />}
        </div>
        <span className={styles.WithdrawAmountInput_title}>
          {t("wallet.withdraw.form.title")}
        </span>
      </div>

      <div className={styles.WithdrawAmountInput_body}>
        <div className={styles.WithdrawAmountInput_limitsRow}>
          <span className={styles.WithdrawAmountInput_amountLabel}>
            {t("wallet.withdraw.form.amountLabel")}
          </span>
          <div className={styles.WithdrawAmountInput_limits}>
            <div className={styles.WithdrawAmountInput_limitItem}>
              <span>{t("wallet.withdraw.form.minLabel")}</span>
              <div className={styles.WithdrawAmountInput_limitValue}>
                {Icon && <Icon />}
                <span>
                  {isConfigLoading ? "—" : numberSpaceFixer(limitFrom)}
                </span>
              </div>
            </div>
            <div className={styles.WithdrawAmountInput_limitItem}>
              <span>{t("wallet.withdraw.form.maxLabel")}</span>
              <div className={styles.WithdrawAmountInput_limitValue}>
                {Icon && <Icon />}
                <span>{isConfigLoading ? "—" : numberSpaceFixer(limitTo)}</span>
              </div>
            </div>
          </div>
        </div>

        <p className={styles.WithdrawAmountInput_description}>
          {t("wallet.withdraw.form.description")}
        </p>

        <div className={styles.WithdrawAmountInput_inputContainer}>
          <div className={styles.WithdrawAmountInput_inputIcon}>
            {Icon && <Icon />}
          </div>
          <input
            type="text"
            inputMode="numeric"
            className={styles.WithdrawAmountInput_input}
            value={amount ? numberSpaceFixer(Number(amount)) : ""}
            onChange={handleInputChange}
            placeholder="0"
          />
        </div>

        <div className={styles.WithdrawAmountInput_percentRow}>
          {PERCENT_SHORTCUTS.map((percent) => (
            <button
              key={percent}
              type="button"
              className={styles.WithdrawAmountInput_percentButton}
              onClick={() => handlePercentClick(percent)}
            >
              {percent}%
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
